import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Music, Zap, Swords, Play, Plus, Trash2, Upload, ExternalLink, QrCode, Users, Settings, List, Tv, FileText, RefreshCw, Pencil, SkipForward, Eye, Trophy, Pause } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { ResultOverlay } from '../../components/ResultOverlay';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');

export default function AdminBlindTest() {
  const [tab, setTab] = useState('dashboard');
  const [overlay, setOverlay] = useState({ show: false, type: 'success', title: '', subtitle: '' });
  const [activeGame, setActiveGame] = useState(null);
  const [answers, setAnswers] = useState([]);
  const wsRef = useRef(null);

  const syncToNewSystem = async (gameData) => {
    const musicQuestions = (gameData.rounds || []).filter(r => r.type === 'music').map(r => ({
      id: r.round_id, title: r.title, artist: r.artist, audioUrl: r.audio_url || '', youtubeUrl: r.youtube_url || '',
      answers: r.answers || [r.title, r.artist].filter(Boolean), category: r.category || '', type: 'written'
    }));
    const quizQuestions = (gameData.rounds || []).filter(r => r.type === 'quiz').map(r => ({
      id: r.round_id, question: r.question, choices: (r.choices || []).map((c, i) => ({ id: String.fromCharCode(65 + i), text: c })),
      correctId: r.correct_indices?.[0] !== undefined ? String.fromCharCode(65 + r.correct_indices[0]) : 'A', explanation: r.explanation || '', type: 'qcm'
    }));
    const statusMap = { lobby: 'lobby', playing: 'round', finished: 'podium' };
    const state = { game: { id: gameData.game_id, code: gameData.code, name: gameData.name, welcomeText: 'Préparez-vous !',
      mode: gameData.settings?.team_mode ? 'team' : 'solo', type: gameData.mode === 'quiz' ? 'quiz' : gameData.mode === 'mixed' ? 'mix' : 'blindtest',
      rounds: (gameData.rounds || []).length, roundDuration: gameData.settings?.timer || 30, autoNext: gameData.settings?.auto_mode ?? true,
      duelsEnabled: gameData.settings?.duels_enabled ?? true, malusEnabled: gameData.settings?.malus_enabled ?? true,
      pointsCorrect: gameData.settings?.pts || 200, pointsBonusFast: gameData.settings?.speed_bonus || 50,
      status: statusMap[gameData.status] || gameData.status || 'lobby', currentRound: gameData.current_round || 0 },
      adminLoggedIn: true, musicQuestions, quizQuestions,
      players: Object.values(gameData.players || {}).map(p => ({ id: p.player_id, name: p.name, score: p.score || 0, avatar: p.avatar || '' })),
      teams: Object.values(gameData.teams || {}).map(t => ({ id: t.team_id, name: t.name, color: t.color, score: t.score || 0 })),
      answers: [], duels: [], events: [], helps: [] };
    try { await api.post('/rsx/push-state', state); } catch (e) { console.error('Sync:', e); }
  };

  const openGame = async (gid) => {
    const { data } = await api.get(`/party/games/${gid}`);
    setActiveGame(data);
    await syncToNewSystem(data);
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      const s = new WebSocket(`${WS_URL}/api/party/ws/admin/${gid}`);
      wsRef.current = s;
      s.onmessage = (e) => { const d = JSON.parse(e.data); if (d.t === 'join') setActiveGame(p => p ? { ...p, players: { ...p.players, [d.p.player_id]: d.p } } : p); if (d.t === 'ans') setAnswers(p => [d, ...p].slice(0, 30)); };
    }
  };

  const startGame = async () => {
    if (!activeGame) return;
    await api.post(`/party/games/${activeGame.game_id}/start`);
    const { data } = await api.get(`/party/games/${activeGame.game_id}`);
    await syncToNewSystem(data); setActiveGame(data);
    setOverlay({ show: true, type: 'party', title: "C'est parti !", subtitle: '' });
    setTimeout(() => doNextRound(data.game_id), 3000);
  };

  const doNextRound = async (gid) => {
    const id = gid || activeGame?.game_id; if (!id) return;
    setAnswers([]);
    try {
      await api.post(`/party/games/${id}/next`);
      const { data: g } = await api.get(`/party/games/${id}`);
      setActiveGame(g); await syncToNewSystem(g);
      const roundIdx = (g.current_round || 1) - 1;
      const cr = g.rounds?.[roundIdx];
      if (cr) {
        let vid = ''; if (cr.youtube_url) { const m = cr.youtube_url.match(/(?:v=|\/)([\w-]{11})/); if (m) vid = m[1]; }
        await api.post('/rsx/emit', { type: 'round-start', round: { num: roundIdx + 1, total: g.rounds.length, title: cr.title, artist: cr.artist, audioUrl: cr.audio_url || '', youtubeId: vid, timer: g.settings?.timer || 30, type: cr.type || 'music' } });
      }
    } catch { toast.error('Plus de manches'); doEndGame(); }
  };

  const doReveal = async () => {
    if (!activeGame) return;
    await api.post(`/party/games/${activeGame.game_id}/reveal`);
    const { data: g } = await api.get(`/party/games/${activeGame.game_id}`);
    const cr = g.rounds?.[(g.current_round || 1) - 1];
    await api.post('/rsx/emit', { type: 'round-reveal', title: cr?.title || '', artist: cr?.artist || '' });
    await syncToNewSystem(g); setActiveGame(g);
    if (g.settings?.auto_mode) setTimeout(() => doNextRound(), 5000);
  };

  const doEndGame = async () => {
    if (!activeGame) return;
    await api.post(`/party/games/${activeGame.game_id}/end`);
    const { data: g } = await api.get(`/party/games/${activeGame.game_id}`);
    await syncToNewSystem(g); await api.post('/rsx/emit', { type: 'game-end' });
    setOverlay({ show: true, type: 'party', title: 'Terminé !', subtitle: '' }); setActiveGame(g);
  };

  const cur = activeGame?.current_round || 0;
  const tot = activeGame?.rounds?.length || 0;

  return (
    <div className="fade-in pb-24" data-testid="admin-blindtest-module">
      <ResultOverlay {...overlay} onClose={() => setOverlay(p => ({ ...p, show: false }))} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>Blind Test / Quiz</h1>
          <p className="text-xs text-[#A1A1AA]">{"Gérez vos parties, musiques et questions"}</p>
        </div>
        <div className="flex gap-2">
          <a href="/api/blindtest/pages/tv.html" target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="border-white/10 rounded-full text-xs"><Tv className="w-3.5 h-3.5 mr-1" />{"Écran TV"}</Button></a>
          <a href="/api/blindtest/" target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="border-white/10 rounded-full text-xs"><ExternalLink className="w-3.5 h-3.5 mr-1" />Hub</Button></a>
        </div>
      </div>

      {/* Partie active - toujours visible */}
      {activeGame && (
        <div className="bg-[#121212] border border-[#D4AF37]/20 rounded-xl p-3 mb-4 flex items-center gap-3 flex-wrap">
          <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] text-xs">{activeGame.code}</Badge>
          <span className="text-sm font-semibold flex-1 truncate">{activeGame.name}</span>
          <Badge className={activeGame.status === 'playing' ? 'bg-green-500/15 text-green-400' : activeGame.status === 'finished' ? 'bg-white/10 text-[#A1A1AA]' : 'bg-yellow-500/15 text-yellow-400'}>{activeGame.status}</Badge>
          <span className="text-[10px] text-[#A1A1AA]">{cur}/{tot}</span>
          <span className="text-[10px] text-[#A1A1AA]">{Object.keys(activeGame.players || {}).length} joueurs</span>
          <a href="/api/blindtest/pages/tv.html" target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="border-blue-500/30 text-blue-400 rounded-full text-[10px] h-7"><Tv className="w-3 h-3 mr-1" />TV</Button></a>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#121212] border border-white/5 h-auto p-1 mb-4 w-full justify-start overflow-x-auto">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black rounded-full text-[10px] px-3 gap-1"><List className="w-3 h-3" />Parties</TabsTrigger>
          <TabsTrigger value="musics" className="data-[state=active]:bg-red-500 data-[state=active]:text-white rounded-full text-[10px] px-3 gap-1"><Music className="w-3 h-3" />Musiques</TabsTrigger>
          <TabsTrigger value="quiz" className="data-[state=active]:bg-blue-500 data-[state=active]:text-white rounded-full text-[10px] px-3 gap-1"><Zap className="w-3 h-3" />Quiz</TabsTrigger>
          <TabsTrigger value="duels" className="data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-full text-[10px] px-3 gap-1"><Swords className="w-3 h-3" />Duels</TabsTrigger>
          <TabsTrigger value="settings" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black rounded-full text-[10px] px-3 gap-1"><Settings className="w-3 h-3" />Config</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard"><PartiesDashboard overlay={overlay} setOverlay={setOverlay} openGame={openGame} activeGame={activeGame} answers={answers} /></TabsContent>
        <TabsContent value="musics"><BiblioMusiques /></TabsContent>
        <TabsContent value="quiz"><BiblioQuiz /></TabsContent>
        <TabsContent value="duels"><BiblioDuels /></TabsContent>
        <TabsContent value="settings"><ConfigMalus /></TabsContent>
      </Tabs>

      {/* BOUTON FIXE EN BAS — TOUJOURS VISIBLE */}
      {activeGame && (
        <div className="fixed bottom-0 left-0 right-0 z-40 p-3 lg:pl-64 safe-area-bottom" style={{ background: 'linear-gradient(to top, #050505 60%, transparent)' }}>
          <div className="max-w-4xl mx-auto flex gap-2 justify-center items-center">
            <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] shrink-0">{activeGame.code}</Badge>
            {activeGame.status === 'lobby' && (
              <Button onClick={startGame} disabled={!tot} className="bg-green-500 hover:bg-green-600 text-white rounded-full px-8 h-12 font-bold shadow-lg text-base"><Play className="w-5 h-5 mr-2" />LANCER LA PARTIE</Button>
            )}
            {activeGame.status === 'playing' && <>
              <Button onClick={() => doNextRound()} className="bg-[#D4AF37] hover:bg-[#F9E5C0] text-black rounded-full px-6 h-12 font-bold shadow-lg"><SkipForward className="w-5 h-5 mr-2" />Suivante</Button>
              <Button onClick={doReveal} variant="outline" className="border-white/20 text-white rounded-full px-5 h-12 shadow-lg"><Eye className="w-5 h-5 mr-2" />Révéler</Button>
              <Button onClick={doEndGame} variant="outline" className="border-red-500/30 text-red-400 rounded-full px-5 h-12 shadow-lg"><Trophy className="w-5 h-5 mr-2" />Fin</Button>
            </>}
            {activeGame.status === 'finished' && <p className="text-[#A1A1AA] text-sm">Partie terminée</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// ━━━ Parties Dashboard ━━━
function PartiesDashboard({ overlay, setOverlay, openGame, activeGame, answers }) {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: 'Soirée RSX', mode: 'blindtest', timer: 30, pts: 200, team_mode: false, duels_enabled: true, malus_enabled: true, auto_mode: true });

  useEffect(() => { load(); }, []);
  const load = () => api.get('/party/games').then(r => setGames(r.data)).catch(() => {}).finally(() => setLoading(false));

  const create = async () => {
    try {
      const { data } = await api.post('/party/games', form);
      setOverlay({ show: true, type: 'party', title: 'Partie créée !', subtitle: `Code: ${data.code}` });
      setCreateOpen(false); load(); openGame(data.game_id);
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spin-gold w-6 h-6" /></div>;

  // If a game is active, show its details
  if (activeGame) {
    const pl = Object.values(activeGame.players || {}).sort((a, b) => b.score - a.score);
    const joinWithCode = `https://vibzz.app/join?code=${activeGame.code}`;
    return (
      <div>
        {/* QR Code */}
        <div className="bg-[#121212] border border-[#D4AF37]/20 rounded-2xl p-5 mb-4">
          <div className="flex flex-col sm:flex-row items-center gap-5">
            <img src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(joinWithCode)}&bgcolor=121212&color=D4AF37`} alt="QR" className="rounded-xl shrink-0" />
            <div className="flex-1 text-center sm:text-left">
              <p className="text-xs text-[#A1A1AA] mb-1">Code de la partie</p>
              <p className="text-4xl font-black tracking-[0.3em] text-[#D4AF37]" style={{ fontFamily: 'JetBrains Mono' }}>{activeGame.code}</p>
              <p className="text-xs text-[#A1A1AA] mt-2">vibzz.app/join</p>
              <div className="flex gap-2 mt-2 justify-center sm:justify-start">
                <Button size="sm" variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] rounded-full text-[10px]" onClick={() => { navigator.clipboard.writeText(activeGame.code); toast.success('Code copié !'); }}>Copier code</Button>
                <Button size="sm" variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] rounded-full text-[10px]" onClick={() => { navigator.clipboard.writeText(joinWithCode); toast.success('Lien copié !'); }}>Copier lien</Button>
              </div>
            </div>
          </div>
        </div>
        {/* Answers + Players */}
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="bg-[#121212] border border-white/5 rounded-xl p-4">
            <p className="text-[10px] text-[#A1A1AA] font-semibold mb-2">RÉPONSES LIVE</p>
            {!answers?.length ? <p className="text-[10px] text-[#555] text-center py-4">En attente...</p> : answers.map((a, i) => (
              <div key={`${a.pid}-${i}`} className="flex items-center gap-2 py-1 border-b border-white/5 last:border-0">
                <span className="text-[10px] font-medium w-16 truncate">{a.name}</span>
                <span className="text-[9px] text-[#A1A1AA] flex-1 truncate">{a.a || `Choix ${a.choice}`}</span>
                <Badge className={a.ok ? 'bg-green-500/15 text-green-400 text-[8px]' : 'bg-red-500/15 text-red-400 text-[8px]'}>{a.ok ? `+${a.pts}` : 'X'}</Badge>
              </div>
            ))}
          </div>
          <div className="bg-[#121212] border border-white/5 rounded-xl p-4">
            <p className="text-[10px] text-[#A1A1AA] font-semibold mb-2"><Users className="w-3 h-3 inline mr-1" />JOUEURS ({pl.length})</p>
            {pl.map((p, i) => (
              <div key={p.player_id} className="flex items-center gap-2 p-1.5 mb-1 rounded-lg bg-white/[0.02]">
                <span className="text-[9px] font-bold w-4" style={{ color: i === 0 ? '#D4AF37' : '#A1A1AA' }}>{i + 1}</span>
                <span className="text-[10px] flex-1 truncate font-medium">{p.name}</span>
                <span className="text-[10px] font-black" style={{ fontFamily: 'JetBrains Mono' }}>{p.score}</span>
              </div>
            ))}
            {!pl.length && <p className="text-[10px] text-[#555] text-center py-3">En attente de joueurs...</p>}
          </div>
        </div>
      </div>
    );
  }

  // No active game — show list
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[#A1A1AA]">{games.length} partie(s)</p>
        <Button onClick={() => setCreateOpen(true)} className="bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full px-5 text-xs"><Plus className="w-3.5 h-3.5 mr-1" />Nouvelle partie</Button>
      </div>
      {games.map(g => (
        <div key={g.game_id} className="bg-[#121212] border border-white/5 rounded-xl p-4 mb-2 flex items-center gap-4 cursor-pointer card-hover" onClick={() => openGame(g.game_id)}>
          <Music className="w-5 h-5 text-[#D4AF37]" />
          <div className="flex-1"><div className="font-semibold text-sm">{g.name}</div><div className="text-[10px] text-[#A1A1AA]">{g.code} | {g.mode} | {g.rounds?.length || 0} manches | {Object.keys(g.players || {}).length} joueurs</div></div>
          <Badge className={g.status === 'playing' ? 'bg-green-500/15 text-green-400' : g.status === 'finished' ? 'bg-white/10 text-[#A1A1AA]' : 'bg-yellow-500/15 text-yellow-400'}>{g.status}</Badge>
        </div>
      ))}
      {!games.length && <p className="text-center text-[#A1A1AA] py-12">{"Créez votre première partie"}</p>}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md"><DialogHeader><DialogTitle>Nouvelle partie</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><Label className="text-xs text-[#A1A1AA]">Nom</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div>
            <div><Label className="text-xs text-[#A1A1AA]">Type</Label><Select value={form.mode} onValueChange={v => setForm(p => ({ ...p, mode: v }))}><SelectTrigger className="bg-[#121212] border-white/10 rounded-xl mt-1"><SelectValue /></SelectTrigger><SelectContent className="bg-[#121212] border-white/10"><SelectItem value="blindtest">Blind Test</SelectItem><SelectItem value="quiz">Quiz</SelectItem><SelectItem value="mixed">Mixte</SelectItem></SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3"><div><Label className="text-xs text-[#A1A1AA]">{"Durée (sec)"}</Label><Input type="number" value={form.timer} onChange={e => setForm(p => ({ ...p, timer: +e.target.value || 30 }))} className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div><div><Label className="text-xs text-[#A1A1AA]">Points</Label><Input type="number" value={form.pts} onChange={e => setForm(p => ({ ...p, pts: +e.target.value || 200 }))} className="bg-[#121212] border-white/10 rounded-xl mt-1" /></div></div>
            <div className="flex flex-wrap gap-4"><label className="flex items-center gap-2 text-xs"><Switch checked={form.duels_enabled} onCheckedChange={v => setForm(p => ({ ...p, duels_enabled: v }))} />Duels</label><label className="flex items-center gap-2 text-xs"><Switch checked={form.malus_enabled} onCheckedChange={v => setForm(p => ({ ...p, malus_enabled: v }))} />Malus</label><label className="flex items-center gap-2 text-xs"><Switch checked={form.team_mode} onCheckedChange={v => setForm(p => ({ ...p, team_mode: v }))} />{"Équipes"}</label><label className="flex items-center gap-2 text-xs"><Switch checked={form.auto_mode} onCheckedChange={v => setForm(p => ({ ...p, auto_mode: v }))} />Auto</label></div>
            <Button onClick={create} className="w-full bg-[#D4AF37] text-black hover:bg-[#F9E5C0] rounded-full">{"Créer"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ━━━ Biblio Musiques ━━━
function BiblioMusiques() {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState('');
  const [ytUrl, setYtUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [plainText, setPlainText] = useState('');

  useEffect(() => { api.get('/party/games').then(r => { setGames(r.data); if (r.data.length) setSelectedGame(r.data[0].game_id); }).catch(() => {}).finally(() => setLoading(false)); }, []);

  useEffect(() => {
    if (selectedGame) api.get(`/party/games/${selectedGame}`).then(r => setTracks((r.data.rounds || []).filter(x => x.type === 'music'))).catch(() => {});
  }, [selectedGame]);

  const importYt = async () => {
    if (!ytUrl.trim() || !selectedGame) return;
    setImporting(true);
    try { const { data } = await api.post(`/party/games/${selectedGame}/import-youtube`, { url: ytUrl }); toast.success(`${data.count} musiques importées`); setYtUrl(''); refreshTracks(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setImporting(false); }
  };

  const importText = async () => {
    if (!plainText.trim() || !selectedGame) return;
    try { const { data } = await api.post(`/party/games/${selectedGame}/import-text`, { text: plainText, mode: 'blindtest' }); toast.success(`${data.count} morceaux ajoutés`); setPlainText(''); refreshTracks(); }
    catch { toast.error('Erreur import'); }
  };

  const handleUpload = async (file) => {
    if (!file || !selectedGame) return;
    const title = prompt('Titre :'); if (!title) return;
    const artist = prompt('Artiste :') || '';
    setUploading(true);
    try { const fd = new FormData(); fd.append('file', file); fd.append('title', title); fd.append('artist', artist);
      await api.post(`/party/games/${selectedGame}/upload-audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`"${title}" uploadé`); refreshTracks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setUploading(false); }
  };

  const deleteTrack = async (rid) => { await api.delete(`/party/games/${selectedGame}/rounds/${rid}`); refreshTracks(); };
  const refreshTracks = () => { if (selectedGame) api.get(`/party/games/${selectedGame}`).then(r => setTracks((r.data.rounds || []).filter(x => x.type === 'music'))).catch(() => {}); };

  if (loading) return <div className="flex justify-center py-8"><div className="spin-gold w-6 h-6" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <Label className="text-xs text-[#A1A1AA] shrink-0">Partie :</Label>
        <Select value={selectedGame} onValueChange={setSelectedGame}><SelectTrigger className="bg-[#121212] border-white/10 rounded-xl flex-1"><SelectValue placeholder="Choisir..." /></SelectTrigger><SelectContent className="bg-[#121212] border-white/10">{games.map(g => <SelectItem key={g.game_id} value={g.game_id}>{g.name} ({g.code})</SelectItem>)}</SelectContent></Select>
      </div>
      {/* YouTube import */}
      <div className="flex gap-2">
        <Input value={ytUrl} onChange={e => setYtUrl(e.target.value)} placeholder="Lien playlist YouTube..." className="bg-[#0A0A0A] border-white/10 rounded-xl text-xs flex-1" />
        <Button onClick={importYt} disabled={importing || !ytUrl.trim()} size="sm" className="bg-red-500 text-white rounded-full text-xs shrink-0">{importing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <><Music className="w-3 h-3 mr-1" />YouTube</>}</Button>
      </div>
      {/* Audio upload */}
      <div className="relative" onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#D4AF37'; }} onDragLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }} onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; if (e.dataTransfer.files[0]) handleUpload(e.dataTransfer.files[0]); }}>
        <input type="file" accept="audio/*" onChange={e => { if (e.target.files[0]) handleUpload(e.target.files[0]); e.target.value = ''; }} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
        <div className="bg-[#0A0A0A] border-2 border-dashed border-white/10 rounded-xl p-4 text-center"><Upload className="w-5 h-5 text-[#D4AF37] mx-auto mb-1" /><p className="text-[10px] text-[#A1A1AA]">{uploading ? 'Upload...' : 'Glisser un MP3 ou cliquer'}</p></div>
      </div>
      {/* Plain text import */}
      <details className="bg-[#121212] border border-white/5 rounded-xl">
        <summary className="p-3 cursor-pointer text-xs text-[#A1A1AA] flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-green-400" />Import texte / CSV</summary>
        <div className="p-3 pt-0 space-y-2">
          <Textarea value={plainText} onChange={e => setPlainText(e.target.value)} rows={4} placeholder={"TITRE: Alors on danse\nARTISTE: Stromae\nREPONSE: Stromae - Alors on danse\n\nTITRE: Papaoutai\nARTISTE: Stromae"} className="bg-[#0A0A0A] border-white/10 rounded-xl text-xs resize-none font-mono" />
          <Button onClick={importText} disabled={!plainText.trim()} size="sm" className="bg-green-500 text-white rounded-full text-xs"><FileText className="w-3 h-3 mr-1" />Importer</Button>
        </div>
      </details>
      {/* List */}
      <div className="bg-[#121212] border border-white/5 rounded-xl p-3">
        <p className="text-[10px] text-[#A1A1AA] font-semibold mb-2">MUSIQUES ({tracks.length})</p>
        {!tracks.length ? <p className="text-[10px] text-[#555] text-center py-4">Aucune musique</p> : (
          <div className="space-y-1 max-h-64 overflow-y-auto">{tracks.map((t, i) => (
            <div key={t.round_id} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02]">
              <span className="text-[9px] font-bold w-4 text-[#D4AF37]">{i + 1}</span>
              <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate">{t.title}</p><p className="text-[9px] text-[#A1A1AA]">{t.artist}</p></div>
              {t.audio_url && <Badge className="bg-green-500/15 text-green-400 text-[8px]">Audio</Badge>}
              {t.youtube_url && <Badge className="bg-red-500/15 text-red-400 text-[8px]">YT</Badge>}
              <button onClick={() => deleteTrack(t.round_id)} className="text-red-400"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}</div>
        )}
      </div>
    </div>
  );
}

// ━━━ Biblio Quiz ━━━
function BiblioQuiz() {
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState('');
  const [questions, setQuestions] = useState([]);
  const [form, setForm] = useState({ question: '', choices: ['', '', '', ''], correct: 0, explanation: '' });
  const [plainText, setPlainText] = useState('');

  useEffect(() => { api.get('/party/games').then(r => { setGames(r.data); if (r.data.length) setSelectedGame(r.data[0].game_id); }).catch(() => {}); }, []);
  useEffect(() => {
    if (selectedGame) api.get(`/party/games/${selectedGame}`).then(r => setQuestions((r.data.rounds || []).filter(x => x.type === 'quiz'))).catch(() => {});
  }, [selectedGame]);

  const addQuestion = async () => {
    if (!form.question.trim() || !selectedGame) return;
    const choices = form.choices.filter(c => c.trim());
    await api.post(`/party/games/${selectedGame}/rounds`, { type: 'quiz', question: form.question, choices, correct_indices: [parseInt(form.correct)], answers: [choices[form.correct] || ''], explanation: form.explanation, title: '', artist: '' });
    toast.success('Question ajoutée'); setForm({ question: '', choices: ['', '', '', ''], correct: 0, explanation: '' }); refresh();
  };

  const importText = async () => {
    if (!plainText.trim() || !selectedGame) return;
    try { const { data } = await api.post(`/party/games/${selectedGame}/import-text`, { text: plainText, mode: 'quiz' }); toast.success(`${data.count} questions ajoutées`); setPlainText(''); refresh(); }
    catch { toast.error('Erreur'); }
  };

  const deleteQ = async (rid) => { await api.delete(`/party/games/${selectedGame}/rounds/${rid}`); refresh(); };
  const refresh = () => { if (selectedGame) api.get(`/party/games/${selectedGame}`).then(r => setQuestions((r.data.rounds || []).filter(x => x.type === 'quiz'))).catch(() => {}); };

  const qColors = ['#DC2626', '#3B82F6', '#10B981', '#F59E0B'];

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <Label className="text-xs text-[#A1A1AA] shrink-0">Partie :</Label>
        <Select value={selectedGame} onValueChange={setSelectedGame}><SelectTrigger className="bg-[#121212] border-white/10 rounded-xl flex-1"><SelectValue /></SelectTrigger><SelectContent className="bg-[#121212] border-white/10">{games.map(g => <SelectItem key={g.game_id} value={g.game_id}>{g.name}</SelectItem>)}</SelectContent></Select>
      </div>
      {/* Form */}
      <div className="bg-[#121212] border border-white/5 rounded-xl p-4 space-y-3">
        <div><Label className="text-xs text-[#A1A1AA]">Question</Label><Input value={form.question} onChange={e => setForm(p => ({ ...p, question: e.target.value }))} className="bg-[#0A0A0A] border-white/10 rounded-xl mt-1" placeholder="Quelle est..." /></div>
        <div className="grid grid-cols-2 gap-2">{form.choices.map((c, i) => (
          <div key={`qc${i}`} className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm shrink-0" style={{ background: qColors[i] }} /><Input value={c} onChange={e => { const ch = [...form.choices]; ch[i] = e.target.value; setForm(p => ({ ...p, choices: ch })); }} className="bg-[#0A0A0A] border-white/10 rounded-lg text-xs h-8" placeholder={`Réponse ${String.fromCharCode(65 + i)}`} /></div>
        ))}</div>
        <div className="flex gap-3 items-center">
          <Label className="text-[10px] text-[#A1A1AA]">Bonne :</Label>
          <Select value={String(form.correct)} onValueChange={v => setForm(p => ({ ...p, correct: parseInt(v) }))}><SelectTrigger className="bg-[#0A0A0A] border-white/10 rounded-lg w-16 h-7 text-[10px]"><SelectValue /></SelectTrigger><SelectContent className="bg-[#121212] border-white/10"><SelectItem value="0">A</SelectItem><SelectItem value="1">B</SelectItem><SelectItem value="2">C</SelectItem><SelectItem value="3">D</SelectItem></SelectContent></Select>
          <Input value={form.explanation} onChange={e => setForm(p => ({ ...p, explanation: e.target.value }))} className="bg-[#0A0A0A] border-white/10 rounded-lg text-xs h-7 flex-1" placeholder="Explication (optionnel)" />
        </div>
        <Button onClick={addQuestion} size="sm" className="bg-blue-500 text-white rounded-full text-xs"><Plus className="w-3 h-3 mr-1" />Ajouter</Button>
      </div>
      {/* Import */}
      <details className="bg-[#121212] border border-white/5 rounded-xl">
        <summary className="p-3 cursor-pointer text-xs text-[#A1A1AA] flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-green-400" />Import texte</summary>
        <div className="p-3 pt-0 space-y-2">
          <Textarea value={plainText} onChange={e => setPlainText(e.target.value)} rows={4} placeholder={"QUESTION: Capitale de la France ?\nA: Paris\nB: Lyon\nBONNE_REPONSE: A"} className="bg-[#0A0A0A] border-white/10 rounded-xl text-xs resize-none font-mono" />
          <Button onClick={importText} disabled={!plainText.trim()} size="sm" className="bg-green-500 text-white rounded-full text-xs">Importer</Button>
        </div>
      </details>
      {/* List */}
      <div className="bg-[#121212] border border-white/5 rounded-xl p-3">
        <p className="text-[10px] text-[#A1A1AA] font-semibold mb-2">QUESTIONS ({questions.length})</p>
        {questions.map((q, i) => (
          <div key={q.round_id} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02] mb-1">
            <span className="text-[9px] font-bold w-4 text-blue-400">{i + 1}</span>
            <span className="text-xs flex-1 truncate">{q.question}</span>
            <Badge className="bg-blue-500/15 text-blue-400 text-[8px]">{q.choices?.length || 0} choix</Badge>
            <button onClick={() => deleteQ(q.round_id)} className="text-red-400"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ━━━ Biblio Duels ━━━
function BiblioDuels() {
  const [tracks, setTracks] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get('/party/duel-tracks').then(r => setTracks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    const title = prompt('Titre :'); if (!title) return;
    const artist = prompt('Artiste :') || '';
    setUploading(true);
    try { const fd = new FormData(); fd.append('file', file); fd.append('title', title); fd.append('artist', artist);
      await api.post('/party/duel-tracks/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Ajouté'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setUploading(false); }
  };

  const addManual = async () => {
    const title = prompt('Titre :'); if (!title) return;
    const artist = prompt('Artiste :') || '';
    await api.post('/party/duel-tracks', { title, artist }); toast.success('Ajouté'); load();
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-[#A1A1AA]">{"Musiques utilisées exclusivement pendant les duels entre joueurs"}</p>
      <div className="relative" onDragOver={e => { e.preventDefault(); }} onDrop={e => { e.preventDefault(); if (e.dataTransfer.files[0]) handleUpload(e.dataTransfer.files[0]); }}>
        <input type="file" accept="audio/*" onChange={e => { if (e.target.files[0]) handleUpload(e.target.files[0]); e.target.value = ''; }} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
        <div className="bg-[#0A0A0A] border-2 border-dashed border-white/10 rounded-xl p-4 text-center"><Upload className="w-5 h-5 text-red-400 mx-auto mb-1" /><p className="text-[10px] text-[#A1A1AA]">{uploading ? 'Upload...' : 'Glisser un MP3 duel ou cliquer'}</p></div>
      </div>
      <Button onClick={addManual} variant="outline" size="sm" className="border-white/10 rounded-full text-xs w-full"><Plus className="w-3 h-3 mr-1" />Ajouter manuellement</Button>
      <div className="bg-[#121212] border border-white/5 rounded-xl p-3">
        <p className="text-[10px] text-[#A1A1AA] font-semibold mb-2">MUSIQUES DUEL ({tracks.length})</p>
        {tracks.map((t, i) => (
          <div key={t.track_id} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02] mb-1">
            <span className="text-[9px] font-bold w-4 text-red-400">{i + 1}</span>
            <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate">{t.title}</p><p className="text-[9px] text-[#A1A1AA]">{t.artist}</p></div>
            <button onClick={() => { api.put(`/party/duel-tracks/${t.track_id}`, { active: !t.active }); load(); }} className={`text-[8px] ${t.active ? 'text-green-400' : 'text-red-400'}`}>{t.active ? 'ON' : 'OFF'}</button>
            <button onClick={() => { api.delete(`/party/duel-tracks/${t.track_id}`); load(); }} className="text-red-400"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ━━━ Config Malus ━━━
function ConfigMalus() {
  const malus = [
    { id: 'block', name: 'Bloquer 1 manche', icon: '🚫', desc: 'Le joueur ne peut pas répondre pendant 1 manche' },
    { id: 'minus', name: '-50% points round', icon: '💢', desc: 'Mauvaise réponse = perte de 50% des points du round' },
    { id: 'slow', name: 'Temps réduit', icon: '⏳', desc: 'Temps de réponse divisé par 2' },
    { id: 'reverse', name: 'Inverser QCM', icon: '🔀', desc: 'Les boutons A/B/C/D sont mélangés' },
    { id: 'mute', name: 'Anti-Buzz', icon: '🔇', desc: 'Impossible de buzzer cette manche' },
  ];
  return (
    <div className="space-y-4">
      <p className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>Types de malus</p>
      <p className="text-xs text-[#A1A1AA]">{"Ces malus sont disponibles pour les joueurs pendant la partie (si activés dans la config de la partie)"}</p>
      <div className="space-y-2">{malus.map(m => (
        <div key={m.id} className="bg-[#121212] border border-white/5 rounded-xl p-4 flex items-center gap-4">
          <span className="text-2xl">{m.icon}</span>
          <div className="flex-1"><p className="text-sm font-semibold">{m.name}</p><p className="text-[10px] text-[#A1A1AA]">{m.desc}</p></div>
          <Badge className="bg-green-500/15 text-green-400 text-[9px]">Actif</Badge>
        </div>
      ))}</div>
      <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/15 rounded-xl p-4">
        <p className="text-xs text-[#D4AF37] font-semibold mb-1">Infos</p>
        <p className="text-[10px] text-[#A1A1AA]">{"Les malus sont activables/désactivables par partie lors de la création. Chaque joueur dispose d'un nombre limité de malus défini dans les paramètres de la partie."}</p>
      </div>
    </div>
  );
}
